import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppSelector } from "@/App"
import { Table, ThProps } from "@/components/Table"
import { selectSortedSelectedTeamsWithStats } from "@/store/teamsStats"

import { TeamWithStats } from "../EventsStatistics/TeamStats"
import { EVENTS_TABLE_HEADERS, EventsTableHeader } from "./EventsList"
import cls from "./EventsList.module.scss"
import { EventsListItem } from "./EventsListItem"
import { sortEventsListColumn } from "./sortEventsListColumn"

function sortTeamsByName(a: TeamWithStats, b: TeamWithStats) {
  return a.team.name.localeCompare(b.team.name)
}

export const TeamsList = ({ hiddenColumns }: { hiddenColumns: EventsTableHeader[] }) => {
  const [currentColumnSort, setCurrentColumnSort] = useState<EventsTableHeader | null>(null)
  const [isSortedDesc, setIsSortedDesc] = useState(true)
  const sorter = useMemo(() => {
    if (currentColumnSort) {
      const columnSortCallback = sortEventsListColumn(currentColumnSort, isSortedDesc)
      if (columnSortCallback) return columnSortCallback
    }
    return sortTeamsByName
  }, [currentColumnSort, isSortedDesc])

  const selectedTeamsWithStats = useAppSelector(selectSortedSelectedTeamsWithStats(sorter))

  const { t } = useTranslation()

  function onColumnSort(column: EventsTableHeader) {
    setIsSortedDesc(column === currentColumnSort ? !isSortedDesc : true)
    setCurrentColumnSort(column)
  }

  function getThSortedBy(column: EventsTableHeader): ThProps["sortedBy"] {
    if (currentColumnSort === column) {
      return isSortedDesc ? "desc" : "asc"
    }
  }

  return (
    <Table.Card className={cls.tableCard}>
      <Table
        columns="minmax(200px, 2fr) minmax(110px, 1fr) minmax(140px, 1fr) minmax(140px, 1fr) minmax(125px, 1fr) minmax(140px, 1fr) minmax(120px, 1fr) minmax(150px, 1fr) minmax(120px, 1fr) minmax(100px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr)"
        empty={selectedTeamsWithStats.length === 0}
        minWidth="1590px"
      >
        <Table.Thead>
          <Table.Tr>
            {EVENTS_TABLE_HEADERS.filter((header) => !hiddenColumns.includes(header)).map((header) => (
              <Table.Th sortedBy={getThSortedBy(header)} onClick={() => onColumnSort(header)} key={header}>
                {t(`stats.list.${header}`)}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {selectedTeamsWithStats.map((teamWithStats) => (
            <EventsListItem teamWithStats={teamWithStats} hiddenColumns={hiddenColumns} key={teamWithStats.team.id} />
          ))}
        </Table.Tbody>
      </Table>
    </Table.Card>
  )
}
